import type { CompositionPageScope } from '@revelio/core/global-ai-assistant/composition-page-scope';
import type { CompositionV3Filters } from '@revelio/data-access';
import { countryIdFromNestedGeographyId } from '@revelio/iso-utility';

import {
  type DateFilter,
  type DateRangeFilter,
  type Filter,
  FilterId,
  type FilterSelectionsMap,
  FilterType,
  isMultiFilter,
  isSegmentedFilter,
} from '../CustomDash/filters/types/filters';
import { getNumericIdFromNestedId } from '../CustomDash/filters/utils/get-numeric-id-from-nested-id';
import {
  getV3CompanyFilters,
  RICS_FILTER_KEYS,
} from '../CustomDash/filters/utils/get-v3-company-filters';
import {
  GEOGRAPHY_FILTER_KEYS,
  getV3GeographyFilters,
} from '../CustomDash/filters/utils/get-v3-geography-filters';
import { getV3Taxonomies } from '../CustomDash/filters/utils/get-v3-taxonomies';
import { getListFilter } from '../CustomDash/filters/utils/getListFilter';
import {
  ACTIVITY_LEVEL_ID_TO_FILTER_KEY,
  appendActivitySelectionToFilters,
  appendRoleSelectionToFilters,
  appendSkillSelectionToV3Filters,
  getSkillV3FilterKeyForLevelId,
  ROLE_LEVEL_ID_TO_FILTER_KEY,
} from '../CustomDash/filters/utils/role-skill-ent-kind';

/**
 * The current page's active filters as a {@link CompositionPageScope}: the same
 * `CompositionV3Filters` the page builds for its own charts, plus the companies
 * it is about and the names of whatever could not come along.
 *
 * WHY IT REUSES THE PAGE'S OWN ENCODERS. `appendRoleSelectionToFilters`,
 * `appendActivitySelectionToFilters`, `getV3GeographyFilters`,
 * `getV3CompanyFilters` and `getListFilter` are what this page already calls
 * to draw itself. Calling them here means a read of the
 * page and the page's own charts narrow on the SAME ids under the SAME keys by
 * construction, and there is no second table of filter keys to drift.
 *
 * WHAT IT ADDS ON TOP is the honesty the charts do not need: those encoders
 * drop what they cannot express, and a read that quietly dropped the page's
 * Employment Type filter would answer a broader question than the page shows
 * while claiming to be page-relative. So every selection an encoder declines to
 * take is DISCLOSED by display name instead, and the read repeats those names
 * to the user.
 */

/**
 * Preferences, not page state: the server already has these from the turn's
 * preference envelope. The one family that is neither emitted NOR disclosed —
 * listing them as omitted would tell the user their own saved preferences
 * "were not inherited", which is false.
 */
const ENVELOPE_CARRIED_FILTER_IDS: ReadonlySet<FilterId> = new Set([
  FilterId.ROLE_TAXONOMY,
  FilterId.SKILL_TAXONOMY,
  FilterId.ACTIVITY_TAXONOMY,
  FilterId.GEOGRAPHY_TAXONOMY,
  FilterId.DATA_ADJUSTMENTS,
  FilterId.CONTINGENT_WORKERS,
]);

/**
 * Spelled out rather than imported: the registry would pull every filter
 * definition into this otherwise dependency-free adapter. Matches
 * `definitions/industry.ts`.
 */
const INDUSTRY_FILTER_NAME = 'Industry';

/**
 * Keys spelled as `getCompositionV3Filters` spells them. Gender and ethnicity
 * codes are not vetted here on purpose: `page-scope.ts` is the trust boundary,
 * and a browser-side copy of the code table would silently drop what the
 * server refuses by name.
 */
const LIST_FILTER_KEYS = {
  [FilterId.SENIORITY]: 'seniority',
  [FilterId.EDUCATION]: 'highest_degree',
  [FilterId.GENDER]: 'gender',
  [FilterId.ETHNICITY]: 'ethnicity',
} as const satisfies Partial<Record<FilterId, keyof CompositionV3Filters>>;

type UnwrappedPageFilter = { filter: Filter; displayName: string };

function unwrapSegmented(filter: Filter): Filter {
  return isSegmentedFilter(filter) ? filter.wrappedFilter : filter;
}

/**
 * A `MULTI_*` filter keeps its narrowings in `instances`, each carrying its own
 * `isNot` / `isUserSubmitted`. Reading only the wrapper would make a
 * multi-instance role filter contribute nothing; merging them (as
 * `flattenMultiFiltersForTooltip` does for display) would lose the per-instance
 * negation this adapter acts on.
 */
function flattenPageFilterInstances(
  activeFilterMap: FilterSelectionsMap
): UnwrappedPageFilter[] {
  const instances: UnwrappedPageFilter[] = [];
  for (const [filterId, filter] of activeFilterMap) {
    if (ENVELOPE_CARRIED_FILTER_IDS.has(filterId)) continue;
    const unwrapped = unwrapSegmented(filter);
    if (!isMultiFilter(unwrapped)) {
      instances.push({ filter: unwrapped, displayName: filter.name });
      continue;
    }
    for (const instance of unwrapped.instances) {
      instances.push({
        filter: unwrapSegmented(instance),
        // The wrapper names the dimension ("Role"); an instance may not.
        displayName: instance.name || unwrapped.name,
      });
    }
  }
  return instances;
}

/** A filter the user cleared is not page state, so it must not be disclosed
 * as un-inherited either. */
function hasActiveSelection(filter: Filter): boolean {
  switch (filter.type) {
    case FilterType.TREE:
    case FilterType.LIST:
    case FilterType.STATIC_LIST:
      return filter.selections.length > 0;
    case FilterType.FREE_TEXT:
      return filter.text.length > 0;
    case FilterType.TEXT:
      return filter.value.trim().length > 0;
    case FilterType.SCHOOL:
      return Object.keys(filter.schoolResultItems).length > 0;
    case FilterType.RANGE:
      return filter.min != null || filter.max != null;
    case FilterType.DATE_RANGE:
      return filter.min != null || filter.max != null;
    case FilterType.DATE:
      return filter.value != null;
    case FilterType.GROUP:
      return false;
    default:
      return true;
  }
}

function isNegated(filter: Filter): boolean {
  return 'isNot' in filter && filter.isNot === true;
}

/**
 * "Previously at Google", "previously an Engineer". No filter definition
 * defaults `isCurrent` to `false`, so an explicit `false` is the user's toggle.
 */
function isPrevious(filter: Filter): boolean {
  return 'isCurrent' in filter && filter.isCurrent === false;
}

/** `'2024-01-31'` from a UTC day. Null rather than the `"Invalid Date"` string
 * the server would refuse. */
function toIsoDayBound(date: Date | null): string | null {
  if (!date || Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
}

/** How many ids a filter slice carries, for comparing against how many
 * selections went in — see {@link tookEverySelection}. */
function countIds(slice: Record<string, unknown>): number {
  let total = 0;
  for (const value of Object.values(slice)) {
    if (Array.isArray(value)) total += value.length;
  }
  return total;
}

/**
 * Whether an encoder took every selection it was given.
 *
 * The shared encoders answer "can this be expressed?" by simply not emitting
 * what they cannot — a level with no composition key, an id that will not
 * parse. Counting what came back is how this adapter learns that WITHOUT
 * keeping its own copy of their tables, which is the copy that would drift.
 */
function tookEverySelection(
  slice: Record<string, unknown>,
  selectionCount: number
): boolean {
  return countIds(slice) === selectionCount;
}

/**
 * APPENDS, never replaces. A `MULTI_*` filter arrives here as one instance per
 * chip, and two chips of one dimension are two values of a single composition
 * key — so a second London-then-New-York instance that overwrote the first
 * would publish a page narrowed to New York alone, and the read would answer
 * confidently for a population the page never showed.
 */
function mergeIdKeys(
  into: CompositionV3Filters,
  slice: Record<string, unknown>
): CompositionV3Filters {
  const merged: Record<string, unknown> = { ...into };
  for (const [key, value] of Object.entries(slice)) {
    if (!Array.isArray(value)) continue;
    const existing = merged[key];
    merged[key] = Array.isArray(existing) ? [...existing, ...value] : value;
  }
  return merged as CompositionV3Filters;
}

/**
 * A tree selection's own level id as the `CompositionV3Filters` key it lands
 * on, for the dimensions a page can be ABOUT.
 *
 * Every list comes from the encoder that owns it — `ROLE_LEVEL_ID_TO_FILTER_KEY`
 * maps role levels, `getSkillV3FilterKeyForLevelId` skill levels (the key the
 * CUSTOM_SKILL case below files them under), `ACTIVITY_LEVEL_ID_TO_FILTER_KEY`
 * activity levels, and geography and industry level ids ARE their key — so a
 * level added to the v3 schema is still one edit, in the encoder.
 *
 * Deliberately partial: a level with no key here contributes no primary
 * selection, which costs a name and never a wrong one.
 */
const SELF_NAMED_LEVEL_KEYS: ReadonlySet<string> = new Set([
  ...GEOGRAPHY_FILTER_KEYS,
  ...RICS_FILTER_KEYS,
]);

function compositionFilterKeyForLevelId(
  levelId: string
): keyof CompositionV3Filters | null {
  if (levelId === 'company') return 'company';
  const roleKey = (
    ROLE_LEVEL_ID_TO_FILTER_KEY as Record<
      string,
      keyof CompositionV3Filters | undefined
    >
  )[levelId];
  if (roleKey) return roleKey;
  const skillKey = getSkillV3FilterKeyForLevelId(levelId);
  if (skillKey) return skillKey;
  const activityKey = (
    ACTIVITY_LEVEL_ID_TO_FILTER_KEY as Record<
      string,
      keyof CompositionV3Filters | undefined
    >
  )[levelId];
  if (activityKey) return activityKey;
  return SELF_NAMED_LEVEL_KEYS.has(levelId)
    ? (levelId as keyof CompositionV3Filters)
    : null;
}

export type PageScopeSubject = {
  /** The page subject is inherited even when navigation set it without a filter submission. */
  primaryFilterId?: FilterId;
  /** The effective page taxonomy can differ from the account preference. */
  geographyTaxonomyId: number;
  /** Resolved by the chart filter pipeline, including presets and account limits. */
  effectiveDateFilter?: DateRangeFilter | DateFilter | null;
};

export function buildCurrentPageScope(
  activeFilterMap: FilterSelectionsMap,
  page: PageScopeSubject
): CompositionPageScope {
  const { primaryFilterId, geographyTaxonomyId } = page;
  let filters: CompositionV3Filters = {};
  const companies: Array<{ rcid: number; name: string }> = [];
  const omittedFilterNames: string[] = [];
  const seenOmitted = new Set<string>();
  let dateRange: { start: string | null; end: string | null } | null = null;
  const postingSource: number[] = [];
  const overallRating: number[] = [];
  type LabelRow = {
    id: string;
    level: string;
    taxonomy_id: number;
    name: string;
  };
  const roleNames: LabelRow[] = [];
  const activityNames: LabelRow[] = [];
  const geographyNames: LabelRow[] = [];
  const primarySelections: NonNullable<
    CompositionPageScope['primarySelections']
  > = [];
  const countryAncestors: number[] = [];

  const disclose = (displayName: string): void => {
    const trimmed = displayName.trim();
    // Every registry definition has a display name; a nameless filter is
    // dropped rather than disclosed under its internal id, which the note this
    // feeds is forbidden to carry.
    if (!trimmed || seenOmitted.has(trimmed)) return;
    seenOmitted.add(trimmed);
    omittedFilterNames.push(trimmed);
  };

  const scopeFilters = new Map(activeFilterMap);
  if (page.effectiveDateFilter !== undefined) {
    scopeFilters.delete(FilterId.DATE_RANGE);
    scopeFilters.delete(FilterId.SNAPSHOT_DATE);
    if (page.effectiveDateFilter) {
      scopeFilters.set(page.effectiveDateFilter.id, page.effectiveDateFilter);
    }
  }
  for (const { filter, displayName } of flattenPageFilterInstances(
    scopeFilters
  )) {
    if (ENVELOPE_CARRIED_FILTER_IDS.has(filter.id)) continue;
    if (!hasActiveSelection(filter)) continue;
    // The page's subject is rarely "submitted" — the user navigated to it.
    // Elsewhere, a default nobody touched is not a narrowing.
    if (
      filter.id !== FilterId.COMPANY &&
      filter.id !== primaryFilterId &&
      filter.id !== page.effectiveDateFilter?.id &&
      filter.isUserSubmitted !== true
    ) {
      continue;
    }
    // WHICH ids are the page's subject, captured before the encoders below
    // merge this filter's onto the same keys as everything else. Industry is
    // the case that forces it: the dedicated Industry filter and the COMPANY
    // tree's RICS levels both land on `rics_k*`, so afterwards nothing can tell
    // the subject from the narrowing.
    if (filter.id === primaryFilterId && filter.type === FilterType.TREE) {
      for (const selection of filter.selections) {
        const id = getNumericIdFromNestedId(selection.id);
        const key = compositionFilterKeyForLevelId(selection.levelId);
        if (id === null || !key) continue;
        primarySelections.push({ key, id, name: selection.name });
      }
    }
    // The read refuses a negated filter outright, so carrying one would turn a
    // page holding a single "not London" chip into a refusal of the whole read.
    if (isNegated(filter)) {
      disclose(displayName);
      continue;
    }
    // Every id below describes a CURRENT position or location, which is what a
    // composition read counts. A previous-employment filter is a different
    // question, and sending it as a current one does not trip the read's
    // refusal — it defeats it, and the read answers confidently about the wrong
    // population. `/talent-discovery` toggles Current/Previous on company,
    // geography, role and seniority.
    if (isPrevious(filter)) {
      disclose(displayName);
      continue;
    }

    switch (filter.id) {
      case FilterId.COMPANY: {
        if (filter.type !== FilterType.TREE) {
          disclose(displayName);
          break;
        }
        // The company tree carries RICS levels too, and those ARE a filter the
        // read can carry — under the industry filter's own label, so a page
        // mixing companies and industries discloses only the half that stayed
        // behind.
        const { rics_k10, rics_k50, rics_k400 } = getV3CompanyFilters(filter);
        const rics = {
          ...(rics_k10 && { rics_k10 }),
          ...(rics_k50 && { rics_k50 }),
          ...(rics_k400 && { rics_k400 }),
        };
        filters = mergeIdKeys(filters, rics);
        const ricsSelections = filter.selections.filter((selection) =>
          selection.levelId.startsWith('rics_')
        );
        if (!tookEverySelection(rics, ricsSelections.length)) {
          disclose(INDUSTRY_FILTER_NAME);
        }
        for (const selection of filter.selections) {
          if (selection.levelId !== 'company') continue;
          const rcid = getNumericIdFromNestedId(selection.id);
          if (rcid === null) {
            disclose(displayName);
            continue;
          }
          companies.push({ rcid, name: selection.name });
        }
        break;
      }

      // The DEDICATED industry filter. Industry selections live in two places —
      // this filter and the RICS levels of the COMPANY tree above — and the
      // page encodes both onto the same `rics_k*` keys, so both go through
      // `getV3CompanyFilters`. Publishing only the company-tree half left an
      // industry the page applies invisible to the read.
      case FilterId.INDUSTRY: {
        if (filter.type !== FilterType.TREE) {
          disclose(displayName);
          break;
        }
        const { rics_k10, rics_k50, rics_k400 } = getV3CompanyFilters(filter);
        const rics = {
          ...(rics_k10 && { rics_k10 }),
          ...(rics_k50 && { rics_k50 }),
          ...(rics_k400 && { rics_k400 }),
        };
        filters = mergeIdKeys(filters, rics);
        if (!tookEverySelection(rics, filter.selections.length)) {
          disclose(displayName);
        }
        break;
      }

      case FilterId.ROLE: {
        if (filter.type !== FilterType.TREE) {
          disclose(displayName);
          break;
        }
        for (const selection of filter.selections) {
          // Tree selection ids are path-encoded (`4_16_8524`) — parsing the raw
          // string as an id would filter to role 4.
          const id = getNumericIdFromNestedId(selection.id);
          const next =
            id === null
              ? filters
              : (appendRoleSelectionToFilters(
                  filters,
                  selection.levelId,
                  id
                ) as CompositionV3Filters);
          // Reference equality is the encoder's own verdict: it returns the
          // accumulator untouched for a level it has no key for.
          if (next === filters) disclose(displayName);
          else {
            filters = next;
            roleNames.push({
              id: selection.id,
              level: selection.levelId,
              taxonomy_id: 0,
              name: selection.name,
            });
          }
        }
        break;
      }

      case FilterId.CUSTOM_SKILL: {
        if (filter.type !== FilterType.TREE) {
          disclose(displayName);
          break;
        }
        for (const selection of filter.selections) {
          const id = getNumericIdFromNestedId(selection.id);
          const next =
            id === null
              ? filters
              : (appendSkillSelectionToV3Filters({
                  acc: filters,
                  levelId: selection.levelId,
                  id,
                }) as CompositionV3Filters);
          if (next === filters) disclose(displayName);
          else filters = next;
        }
        break;
      }

      case FilterId.ACTIVITY: {
        if (filter.type !== FilterType.TREE) {
          disclose(displayName);
          break;
        }
        for (const selection of filter.selections) {
          const id = getNumericIdFromNestedId(selection.id);
          const next =
            id === null
              ? filters
              : (appendActivitySelectionToFilters(
                  filters,
                  selection.levelId,
                  id
                ) as CompositionV3Filters);
          if (next === filters) disclose(displayName);
          else {
            filters = next;
            activityNames.push({
              id: selection.id,
              level: selection.levelId,
              taxonomy_id: 0,
              name: selection.name,
            });
          }
        }
        break;
      }

      case FilterId.GEOGRAPHY: {
        if (filter.type !== FilterType.TREE) {
          disclose(displayName);
          break;
        }
        const geography = getV3GeographyFilters(filter);
        filters = mergeIdKeys(filters, geography);
        // The country each selection sits under, before `getV3GeographyFilters`
        // flattens the nested id and the ancestry is gone for good. Shared with
        // `getIndeedMinStartDate`, which reads a postings date floor off the
        // same country — three inline copies of this rule is how the read came
        // to quote a shorter window than the page beside it.
        for (const selection of filter.selections) {
          const country = countryIdFromNestedGeographyId(
            selection.id,
            selection.levelId
          );
          if (country !== null && !countryAncestors.includes(country)) {
            countryAncestors.push(country);
          }
        }
        if (!tookEverySelection(geography, filter.selections.length)) {
          disclose(displayName);
        } else {
          for (const selection of filter.selections) {
            geographyNames.push({
              id: selection.id,
              level: selection.levelId,
              taxonomy_id: geographyTaxonomyId,
              name: selection.name,
            });
          }
        }
        break;
      }

      case FilterId.SENIORITY:
      case FilterId.EDUCATION:
      case FilterId.GENDER:
      case FilterId.ETHNICITY: {
        if (filter.type !== FilterType.LIST) {
          disclose(displayName);
          break;
        }
        const key = LIST_FILTER_KEYS[filter.id];
        const listed = getListFilter<CompositionV3Filters>(filter, key);
        const ids = listed[key];
        // `getListFilter` parses every selection id and keeps whatever comes
        // back, NaN included; a list whose ids are not all numbers is disclosed
        // rather than sent as `[null]`.
        if (
          !Array.isArray(ids) ||
          ids.length !== filter.selections.length ||
          ids.some((id) => !Number.isFinite(id))
        ) {
          disclose(displayName);
          break;
        }
        filters = mergeIdKeys(filters, listed);
        break;
      }

      /**
       * The two filters that travel as ROOT fields rather than as
       * `CompositionV3Filters` keys. Both are id-only: a selection id IS the
       * dashboard's option value ('3' = LinkedIn, '4' = a four-star bucket),
       * so there is no tree path to decode and nothing here reformats them.
       *
       * A selection whose id is not a plain integer string is DISCLOSED, not
       * dropped: the server refuses an unknown id by field name, and it can
       * only do that for ids it actually receives.
       */
      case FilterId.POSTINGS_SOURCE:
      case FilterId.OVERALL_RATING: {
        if (filter.type !== FilterType.STATIC_LIST) {
          disclose(displayName);
          break;
        }
        const target =
          filter.id === FilterId.POSTINGS_SOURCE
            ? postingSource
            : overallRating;
        for (const selection of filter.selections) {
          // Digits only, never `Number()`: `Number('')` is 0 and 0 is the
          // unified feed, so an empty id would publish a confident filter on
          // a source the user never chose.
          if (typeof selection.id !== 'string' || !/^\d+$/.test(selection.id)) {
            disclose(displayName);
            continue;
          }
          const id = Number(selection.id);
          if (!target.includes(id)) target.push(id);
        }
        break;
      }

      case FilterId.SNAPSHOT_DATE: {
        if (filter.type !== FilterType.DATE) {
          disclose(displayName);
          break;
        }
        const day = toIsoDayBound(filter.value);
        if (!day) disclose(displayName);
        else dateRange = { start: day, end: day };
        break;
      }

      case FilterId.DATE_RANGE: {
        if (filter.type !== FilterType.DATE_RANGE) {
          disclose(displayName);
          break;
        }
        // ISO DAYS, not months. The reads sharing this scope do not share a
        // cadence — postings reads WEEKS — and truncating here would discard
        // the day before any of them saw it, with no later step able to
        // recover which week was meant.
        const start = toIsoDayBound(filter.min);
        const end = toIsoDayBound(filter.max);
        if (!start && !end) {
          disclose(displayName);
          break;
        }
        dateRange = { start, end };
        break;
      }

      default:
        disclose(displayName);
        break;
    }
  }

  const { role_taxonomy, skill_taxonomy, activity_taxonomy } = getV3Taxonomies(
    Array.from(activeFilterMap.values()),
    activeFilterMap
  );

  return {
    companies,
    filters,
    dateRange,
    // Labels must carry the same taxonomy as the IDs they name.
    roleNames: roleNames.map((row) => ({ ...row, taxonomy_id: role_taxonomy })),
    activityNames: activityNames.map((row) => ({
      ...row,
      taxonomy_id: activity_taxonomy,
    })),
    roleTaxonomyId: role_taxonomy,
    skillTaxonomyId: skill_taxonomy,
    activityTaxonomyId: activity_taxonomy,
    postingSource,
    overallRating,
    geographyNames,
    geographyTaxonomyId,
    omittedFilterNames,
    primarySelections,
    countryAncestors,
  };
}
